import { castName, clock, dept, pipSet, session, tr } from "../../engine";

/** How fast the attending comes in after a phone call, decided by how urgent the clerk sounded. */
export type TripUrgency = "high" | "medium" | "low";
export const isTripUrgency = (x: unknown): x is TripUrgency => x === "high" || x === "medium" || x === "low";

export interface VsTrip {
  urg: TripUrgency;
  start: number;
  eta: number;
  /** Level-specific "already called back" flag. */
  cb: boolean;
}

/** Session fields a level needs to use the trip PIP. */
export interface VsTripVars {
  trip: VsTrip | null;
  vsArriveAt: number | null;
}

/** The attending's drive in, shown in the PIP: [fraction of trip, PIP scene, caption]. */
const TRIP: Record<TripUrgency, [at: number, scene: string, caption: string][]> = {
  high: [
    [0, "wake", tr("被吵醒，衣服隨便抓一件就出門", "Woken up, grabs whatever clothes and heads out")],
    [0.2, "car", tr("闖過兩個黃燈", "Runs two yellow lights")],
    [0.65, "park", tr("車直接停在急診門口", "Parks right at the ER entrance")],
    [0.8, "run", tr("跑過走廊", "Runs down the hallway")],
  ],
  medium: [
    [0, "wake", tr("被吵醒，摸黑找眼鏡", "Woken up, gropes for glasses in the dark")],
    [0.2, "dress", tr("拖鞋穿反了，算了", "Slippers on the wrong feet. Whatever.")],
    [0.35, "car", tr("開車中，邊開邊想你剛剛說的", "Driving, thinking over what you just said")],
    [0.75, "elev", tr("電梯每層都停", "Elevator stops at every floor")],
    [0.9, "run", tr("快步走過走廊", "Hurries down the hallway")],
  ],
  low: [
    [0, "sleep", tr("聽完電話，又躺回去五分鐘", "Hangs up, lies back down for five more minutes")],
    [0.2, "brush", tr("慢慢刷牙", "Brushes teeth, slowly")],
    [0.35, "coffee", tr("在便利商店排隊買咖啡", "In line for coffee at the convenience store")],
    [0.55, "car", tr("開車中，等紅燈", "Driving, waiting at a red light")],
    [0.8, "park", tr("醫院停車場找車位", "Hunting for a spot in the hospital lot")],
    [0.92, "elev", tr("電梯每層都停", "Elevator stops at every floor")],
  ],
};

const S = session<VsTripVars>();

/** Start the trip and show it in the PIP. The caller sets `S.vsArriveAt` and handles arrival in its tick. */
export function startVsTrip(urg: TripUrgency, eta: number): void {
  const trip: VsTrip = { urg, start: S.t, eta, cb: false };
  S.trip = trip;
  const steps = TRIP[urg];
  pipSet({
    sprite: dept.attending,
    title: castName(dept.attending),
    scene: () => {
      const f = Math.min(1, (S.t - trip.start) / trip.eta);
      let cur = steps[0] ?? [0, "run", ""];
      for (const it of steps) if (f >= it[0]) cur = it;
      return { key: cur[1], text: cur[2], urg: urg === "medium" ? undefined : urg };
    },
    eta: () => tr(`預計 ${clock(S.vsArriveAt ?? S.t)} 到`, `ETA ${clock(S.vsArriveAt ?? S.t)}`),
  });
}
